<template>
  <b-card
    no-body
    class="steps-editor border shadow-none mb-0"
  >
    <div class="card-header d-flex flex-wrap align-items-center justify-content-between pb-1 pt-1">
      <div class="d-flex align-items-center mb-50 mb-md-0">
        <div class="avatar avatar-light-primary mr-75 p-50 d-none d-sm-flex">
          <feather-icon
            icon="LayersIcon"
            size="18"
          />
        </div>
        <div>
          <h6 class="mb-25 font-weight-bolder">
            {{ $t('Plan Steps') }}
            <b-badge
              v-if="stepsCount"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ stepsCount }}
            </b-badge>
          </h6>
          <small class="text-muted">{{ $t('Configure intervals and descriptions for preventive maintenance') }}</small>
        </div>
      </div>
      <div class="d-flex flex-wrap align-items-center mt-50 mt-md-0">
        <b-button
          v-if="isEditing"
          size="sm"
          variant="outline-primary"
          class="mr-50 mb-50 mb-md-0"
          @click="$emit('open-import')"
        >
          <feather-icon
            icon="UploadCloudIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('Import steps') }}
        </b-button>
        <span
          v-else
          v-b-tooltip.hover.top="$t('Save the plan before importing steps via spreadsheet')"
          class="d-inline-block mr-50 mb-50 mb-md-0"
        >
          <b-button
            size="sm"
            variant="outline-primary"
            disabled
            style="pointer-events: none;"
          >
            <feather-icon
              icon="UploadCloudIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Import steps') }}
          </b-button>
        </span>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-50 mb-50 mb-md-0"
          :disabled="!stepsCount"
          @click="$emit('duplicate')"
        >
          <feather-icon
            icon="CopyIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('Duplicate last step') }}
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          @click="$emit('add')"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('Add step') }}
        </b-button>
      </div>
    </div>

    <b-card-body class="pt-0">
      <b-input-group
        v-if="stepsCount > 4"
        class="input-group-merge mb-1"
        size="sm"
      >
        <b-input-group-prepend is-text>
          <feather-icon
            icon="SearchIcon"
            size="14"
          />
        </b-input-group-prepend>
        <b-form-input
          :value="query"
          :placeholder="$t('Search step by description, odometer or hourmeter...')"
          @input="$emit('update:query', $event)"
        />
      </b-input-group>

      <div
        v-if="stepsCount === 0"
        class="empty-steps text-center py-2 px-1"
      >
        <div class="avatar avatar-light-secondary mb-1 p-50">
          <feather-icon
            icon="LayersIcon"
            size="24"
            class="text-muted"
          />
        </div>
        <h6 class="mb-25 font-weight-bold">
          {{ $t('No steps added') }}
        </h6>
        <p class="text-muted small mb-1">
          {{ $t('Add the first step of this plan or import via spreadsheet') }}
        </p>
        <div class="d-flex justify-content-center flex-wrap">
          <b-button
            size="sm"
            variant="primary"
            class="mr-50 mb-50"
            @click="$emit('add')"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Add step') }}
          </b-button>
          <b-button
            v-if="isEditing"
            size="sm"
            variant="outline-primary"
            class="mb-50"
            @click="$emit('open-import')"
          >
            <feather-icon
              icon="UploadCloudIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('Import steps') }}
          </b-button>
        </div>
      </div>

      <div
        v-else
        class="steps-list"
      >
        <div
          v-for="(step, index) in steps"
          v-show="matchesQuery(step)"
          :key="step.localKey"
          class="step-item-card"
        >
          <!-- Step Header Bar: Step number + quick actions -->
          <div class="step-card-header d-flex justify-content-between align-items-center mb-75">
            <div class="d-flex align-items-center">
              <b-badge
                variant="light-primary"
                pill
                class="step-badge font-weight-bolder px-75 py-25 font-small-2"
              >
                <feather-icon
                  icon="CheckSquareIcon"
                  size="12"
                  class="mr-25 text-primary"
                />
                {{ $t('Step') }} {{ index + 1 }}
              </b-badge>
              <span
                v-if="step.name"
                class="step-header-title text-truncate font-weight-bold text-dark font-small-2 ml-50 d-none d-sm-inline"
              >
                {{ step.name }}
              </span>
            </div>

            <div class="step-card-actions d-flex align-items-center">
              <b-button
                v-b-tooltip.hover.top="$t('Duplicate last step')"
                size="sm"
                variant="flat-secondary"
                class="btn-icon rounded-circle mr-25"
                @click="$emit('duplicate')"
              >
                <feather-icon
                  icon="CopyIcon"
                  size="14"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover.top="$t('Remove')"
                size="sm"
                variant="flat-danger"
                class="btn-icon rounded-circle"
                @click="$emit('remove', step)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
              </b-button>
            </div>
          </div>

          <!-- Step Fields: Dedicated label directly attached to each input -->
          <b-row>
            <!-- Field: Description -->
            <b-col
              cols="12"
              md="5"
              class="mb-75 mb-md-0"
            >
              <b-form-group
                :label="$t('Description') + ' *'"
                :label-for="`step-name-${step.localKey}`"
                label-class="font-weight-bold font-small-2 text-dark mb-25"
                class="mb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="`step-name-${step.localKey}`"
                  rules="required"
                >
                  <b-input-group
                    size="sm"
                    class="input-group-merge"
                  >
                    <b-input-group-prepend is-text>
                      <feather-icon
                        icon="ToolIcon"
                        size="14"
                      />
                    </b-input-group-prepend>
                    <b-form-input
                      :id="`step-name-${step.localKey}`"
                      v-model="step.name"
                      size="sm"
                      :state="errors.length ? false : null"
                    />
                  </b-input-group>
                  <small class="text-danger font-small-1">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Field: Odometer -->
            <b-col
              cols="12"
              sm="6"
              md="2"
              class="mb-75 mb-md-0"
            >
              <b-form-group
                :label="$t('Odometer')"
                :label-for="`step-odometer-${step.localKey}`"
                label-class="font-weight-bold font-small-2 text-dark mb-25"
                class="mb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="`step-odometer-${step.localKey}`"
                  rules="regex:^([0-9]+)?$"
                >
                  <b-input-group
                    size="sm"
                    append="Km"
                  >
                    <b-form-input
                      :id="`step-odometer-${step.localKey}`"
                      v-model="step.odometer"
                      size="sm"
                      :state="errors.length ? false : null"
                      placeholder="Ex: 10000"
                    />
                  </b-input-group>
                  <small class="text-danger font-small-1">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Field: Hourmeter -->
            <b-col
              cols="12"
              sm="6"
              md="2"
              class="mb-75 mb-md-0"
            >
              <b-form-group
                :label="$t('Hourmeter')"
                :label-for="`step-hourmeter-${step.localKey}`"
                label-class="font-weight-bold font-small-2 text-dark mb-25"
                class="mb-0"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :name="`step-hourmeter-${step.localKey}`"
                  rules="regex:^([0-9]+)?$"
                >
                  <b-input-group
                    size="sm"
                    append="h"
                  >
                    <b-form-input
                      :id="`step-hourmeter-${step.localKey}`"
                      v-model="step.hourmeter"
                      size="sm"
                      :state="errors.length ? false : null"
                      placeholder="Ex: 250"
                    />
                  </b-input-group>
                  <small class="text-danger font-small-1">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Field: Note -->
            <b-col
              cols="12"
              md="3"
            >
              <b-form-group
                :label="$t('Note')"
                :label-for="`step-note-${step.localKey}`"
                label-class="font-weight-bold font-small-2 text-dark mb-25"
                class="mb-0"
              >
                <b-input-group
                  size="sm"
                  class="input-group-merge"
                >
                  <b-input-group-prepend is-text>
                    <feather-icon
                      icon="FileTextIcon"
                      size="14"
                    />
                  </b-input-group-prepend>
                  <b-form-input
                    :id="`step-note-${step.localKey}`"
                    v-model="step.note"
                    size="sm"
                    :placeholder="$t('Observação opcional')"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <p
          v-if="query && !hasVisibleSteps"
          class="text-muted text-center mb-0 mt-1"
        >
          {{ $t('No matching records found') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { computed } from '@vue/composition-api'
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BRow,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import { required, regex } from '@validations'

export default {
  name: 'PlanStepsEditor',
  components: {
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BRow,
    ValidationProvider,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    steps: { type: Array, default: () => [] },
    query: { type: String, default: '' },
    isEditing: { type: Boolean, default: false },
  },
  setup(props) {
    const stepsCount = computed(() => props.steps.length)
    const matchesQuery = step => {
      const query = (props.query || '').trim().toLowerCase()
      if (!query) return true
      return (step.name || '').toLowerCase().includes(query)
        || String(step.odometer || '').includes(query)
        || String(step.hourmeter || '').includes(query)
    }
    const hasVisibleSteps = computed(() => props.steps.some(matchesQuery))

    return {
      stepsCount,
      matchesQuery,
      hasVisibleSteps,
      required,
      regex,
    }
  },
}
</script>

<style lang="scss" scoped>
.step-item-card {
  background-color: #fafafc;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem 0.85rem;
  margin-bottom: 0.65rem;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #d8d6de;
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(34, 41, 47, 0.05);
  }

  &:focus-within {
    border-color: rgba(115, 103, 240, 0.5);
    background-color: #ffffff;
    box-shadow: 0 2px 10px rgba(115, 103, 240, 0.08);
  }
}

.step-card-header {
  min-height: 28px;
}

.step-badge {
  letter-spacing: 0.3px;
}

.step-header-title {
  max-width: 380px;
  color: #5e5873;
}

.btn-icon {
  padding: 0.35rem;
  line-height: 0;
}

.empty-steps {
  background: #f8f8f8;
  border-radius: 0.428rem;
  border: 1px dashed #d8d6de;
}
</style>
